import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserCircleIcon,
  CogIcon,
  WalletIcon,
  ChartBarIcon,
  HeartIcon,
  ClockIcon,
  ShieldCheckIcon,
  DocumentDuplicateIcon,
  PencilIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  TrophyIcon,
  UserGroupIcon,
  SparklesIcon,
  FireIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

interface NFT {
  id: string;
  title: string;
  image: string;
  collection: string;
  price?: number;
  lastSale?: number;
}

interface Activity {
  id: string;
  type: 'bid' | 'sale' | 'list' | 'transfer' | 'stake' | 'vote';
  title: string;
  amount?: number;
  timestamp: Date;
}

const mockNFTs: NFT[] = [
  { id: '1', title: 'Cosmic Voyager #123', image: 'https://picsum.photos/seed/owned1/400/400', collection: 'Cosmic Collection', lastSale: 8.5 },
  { id: '2', title: 'Digital Dreams #456', image: 'https://picsum.photos/seed/owned2/400/400', collection: 'Dream Series', price: 6.2 },
  { id: '3', title: 'Neon City #789', image: 'https://picsum.photos/seed/owned3/400/400', collection: 'Cityscapes', lastSale: 4.8 },
  { id: '4', title: 'Abstract Mind #012', image: 'https://picsum.photos/seed/owned4/400/400', collection: 'Abstract Art', price: 12.0 },
  { id: '5', title: 'Phoenix Rising #1', image: 'https://picsum.photos/seed/owned5/400/400', collection: 'Mythical Beasts', lastSale: 45.0 },
  { id: '6', title: 'Pixel Warrior #234', image: 'https://picsum.photos/seed/owned6/400/400', collection: 'Pixel Warriors', price: 2.5 },
];

const mockActivities: Activity[] = [
  { id: '1', type: 'bid', title: 'Placed bid on Cosmic Voyager #2847', amount: 12.5, timestamp: new Date(Date.now() - 3600000) },
  { id: '2', type: 'sale', title: 'Sold Digital Dreams #156', amount: 8.75, timestamp: new Date(Date.now() - 86400000) },
  { id: '3', type: 'stake', title: 'Staked 1,500 NDH in Silver Pool', amount: 1500, timestamp: new Date(Date.now() - 172800000) },
  { id: '4', type: 'vote', title: 'Voted on Proposal #42', timestamp: new Date(Date.now() - 259200000) },
  { id: '5', type: 'list', title: 'Listed Phoenix Rising #1 for auction', amount: 50.0, timestamp: new Date(Date.now() - 345600000) },
];

const ProfilePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'owned' | 'created' | 'favorites' | 'activity' | 'bids'>('owned');
  const [isEditing, setIsEditing] = useState(false);

  // Mock user data
  const user = {
    address: '0x1234...5678',
    fullAddress: '0x1234567890abcdef1234567890abcdef12345678',
    username: 'CryptoCollector',
    bio: 'Passionate NFT collector and DAO contributor. Building the future of digital art.',
    avatar: 'https://picsum.photos/seed/user-profile/200/200',
    banner: 'https://picsum.photos/seed/user-banner/1200/300',
    verified: true,
    tier: 'silver' as const,
    joinedDate: new Date('2023-06-15'),
    stats: {
      owned: 24,
      created: 8,
      favorites: 156,
      volumeTraded: 245.8,
      totalBids: 89,
      totalSales: 34,
    },
    social: {
      twitter: 'cryptocollector',
      discord: 'CryptoCollector#1234',
    },
    staking: {
      staked: 1500,
      tier: 'silver',
      rewards: 25.5,
    },
    governance: {
      votingPower: 1500,
      proposalsVoted: 12,
      proposalsCreated: 2,
    },
    copyTrading: {
      followers: 234,
      following: 12,
      pnl: 156.8,
      winRate: 68,
    },
  };

  const tierColors = {
    bronze: 'from-amber-700 to-amber-900',
    silver: 'from-gray-400 to-gray-600',
    gold: 'from-yellow-400 to-amber-600',
    platinum: 'from-slate-200 to-slate-400',
  };

  const formatTimeAgo = (date: Date) => {
    const diff = Date.now() - date.getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const activityIcons = {
    bid: ChartBarIcon,
    sale: WalletIcon,
    list: DocumentDuplicateIcon,
    transfer: ArrowTopRightOnSquareIcon,
    stake: ShieldCheckIcon,
    vote: CheckCircleIcon,
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="min-h-screen">
      {/* Banner */}
      <div className="relative h-64 md:h-80">
        <img
          src={user.banner}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-900 to-transparent" />
      </div>

      <div className="container-custom">
        {/* Profile Header */}
        <div className="relative -mt-24 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row items-start md:items-end space-y-4 md:space-y-0 md:space-x-6"
          >
            {/* Avatar */}
            <div className="relative">
              <img
                src={user.avatar}
                alt={user.username}
                className="w-32 h-32 md:w-40 md:h-40 rounded-2xl border-4 border-dark-900 object-cover"
              />
              {user.verified && (
                <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center border-4 border-dark-900">
                  <CheckCircleIcon className="h-5 w-5 text-white" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-3xl font-bold">{user.username}</h1>
                <span className={`px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${tierColors[user.tier]} text-white capitalize`}>
                  {user.tier} Tier
                </span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400 mb-3">
                <span className="font-mono">{user.address}</span>
                <button
                  onClick={() => copyToClipboard(user.fullAddress)}
                  className="p-1 hover:text-white transition-colors"
                >
                  <DocumentDuplicateIcon className="h-4 w-4" />
                </button>
              </div>
              <p className="text-gray-400 max-w-xl mb-4">{user.bio}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span>Joined {user.joinedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                {user.social.twitter && (
                  <a href={`https://twitter.com/${user.social.twitter}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary-400">
                    @{user.social.twitter}
                  </a>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsEditing(true)}
                className="btn-secondary flex items-center space-x-2"
              >
                <PencilIcon className="h-4 w-4" />
                <span>Edit Profile</span>
              </button>
              <button className="btn-primary flex items-center space-x-2">
                <CogIcon className="h-4 w-4" />
                <span>Settings</span>
              </button>
            </div>
          </motion.div>
        </div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8"
        >
          {[
            { label: 'Owned', value: user.stats.owned, icon: WalletIcon },
            { label: 'Created', value: user.stats.created, icon: SparklesIcon },
            { label: 'Favorites', value: user.stats.favorites, icon: HeartIcon },
            { label: 'Volume Traded', value: `${user.stats.volumeTraded} ETH`, icon: ChartBarIcon },
            { label: 'Total Bids', value: user.stats.totalBids, icon: FireIcon },
            { label: 'Total Sales', value: user.stats.totalSales, icon: TrophyIcon },
          ].map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="glass-card p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon className="h-5 w-5 text-primary-400" />
                  <span className="text-sm text-gray-400">{stat.label}</span>
                </div>
                <p className="text-xl font-bold">{stat.value}</p>
              </div>
            );
          })}
        </motion.div>

        {/* Additional Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          {/* Staking Stats */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
              <ShieldCheckIcon className="h-5 w-5 text-green-400" />
              <span>Staking</span>
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Staked</span>
                <span className="font-semibold">{user.staking.staked.toLocaleString()} NDH</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Tier</span>
                <span className="font-semibold capitalize">{user.staking.tier}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Rewards</span>
                <span className="font-semibold text-green-400">+{user.staking.rewards} NDH</span>
              </div>
            </div>
            <Link to="/staking" className="block mt-4 text-center text-primary-400 hover:text-primary-300 text-sm">
              View Staking Dashboard
            </Link>
          </div>

          {/* Governance Stats */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
              <CheckCircleIcon className="h-5 w-5 text-purple-400" />
              <span>Governance</span>
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Voting Power</span>
                <span className="font-semibold">{user.governance.votingPower.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Proposals Voted</span>
                <span className="font-semibold">{user.governance.proposalsVoted}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Proposals Created</span>
                <span className="font-semibold">{user.governance.proposalsCreated}</span>
              </div>
            </div>
            <Link to="/governance" className="block mt-4 text-center text-primary-400 hover:text-primary-300 text-sm">
              View Governance
            </Link>
          </div>

          {/* Copy Trading Stats */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
              <UserGroupIcon className="h-5 w-5 text-amber-400" />
              <span>Social Trading</span>
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Followers</span>
                <span className="font-semibold">{user.copyTrading.followers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">PnL</span>
                <span className="font-semibold text-green-400">+{user.copyTrading.pnl} ETH</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Win Rate</span>
                <span className="font-semibold">{user.copyTrading.winRate}%</span>
              </div>
            </div>
            <Link to="/leaderboard" className="block mt-4 text-center text-primary-400 hover:text-primary-300 text-sm">
              View Leaderboard
            </Link>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center space-x-1 border-b border-white/10 mb-8 overflow-x-auto">
            {[
              { id: 'owned', label: 'Owned', count: user.stats.owned },
              { id: 'created', label: 'Created', count: user.stats.created },
              { id: 'favorites', label: 'Favorites', count: user.stats.favorites },
              { id: 'bids', label: 'Active Bids', count: 5 },
              { id: 'activity', label: 'Activity' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-6 py-4 text-sm font-medium transition-colors relative whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className="ml-2 text-gray-500">({tab.count})</span>
                )}
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="profileActiveTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-accent-500"
                  />
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {(activeTab === 'owned' || activeTab === 'created' || activeTab === 'favorites') && (
              <motion.div
                key={activeTab}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {mockNFTs.map((nft) => (
                  <Link
                    key={nft.id}
                    to={`/nft/${nft.id}`}
                    className="glass-card overflow-hidden group hover:border-primary-500/50 transition-all duration-300"
                  >
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={nft.image}
                        alt={nft.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-gray-500 mb-1">{nft.collection}</p>
                      <h3 className="font-semibold group-hover:text-primary-400 transition-colors truncate">
                        {nft.title}
                      </h3>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                        {nft.price ? (
                          <div>
                            <p className="text-xs text-gray-500">Listed for</p>
                            <p className="font-semibold">{nft.price} ETH</p>
                          </div>
                        ) : nft.lastSale ? (
                          <div>
                            <p className="text-xs text-gray-500">Last sale</p>
                            <p className="font-semibold">{nft.lastSale} ETH</p>
                          </div>
                        ) : (
                          <div>
                            <p className="text-xs text-gray-500">Not listed</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </motion.div>
            )}

            {activeTab === 'activity' && (
              <motion.div
                key="activity"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                {mockActivities.map((activity) => {
                  const Icon = activityIcons[activity.type];
                  return (
                    <div
                      key={activity.id}
                      className="glass-card p-4 flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-3 rounded-xl bg-primary-500/10">
                          <Icon className="h-6 w-6 text-primary-400" />
                        </div>
                        <div>
                          <p className="font-medium">{activity.title}</p>
                          <p className="text-sm text-gray-500">{formatTimeAgo(activity.timestamp)}</p>
                        </div>
                      </div>
                      {activity.amount && (
                        <p className="font-semibold">
                          {activity.type === 'stake' ? `${activity.amount} NDH` : `${activity.amount} ETH`}
                        </p>
                      )}
                    </div>
                  );
                })}
              </motion.div>
            )}

            {activeTab === 'bids' && (
              <motion.div
                key="bids"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-12"
              >
                <ChartBarIcon className="h-16 w-16 mx-auto text-gray-500 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No active bids</h3>
                <p className="text-gray-400 mb-6">Your active bids will appear here</p>
                <Link to="/auctions" className="btn-primary">
                  Browse Auctions
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
